import { PERMISSION, ROLE } from '@app/constants/permission'

import { REGISTRY_PAGE, CONTRACTOR_SECTION_PAGE, PROCESSING_RESULTS_PAGE } from '@app/constants/page'

import { isNull } from '@app/utils'
import { arrayUniqueValues } from '@app/utils/arrays'

import type { HasHelperOptions } from './types'
import type { Permission, Role, PermittedRoute } from '@app/types'

const rolesPermissions: Record<Role, Permission[]> = {
  [ROLE.serviceAccount]: Object.values(PERMISSION),
  [ROLE.administrator]: Object.values(PERMISSION),

  [ROLE.priceAnalyticsSpecialist]: [
    PERMISSION.viewMonitoringListsRegistry,
    PERMISSION.createAndCopyMonitoringList,
    PERMISSION.approveMonitoringList,
    PERMISSION.deleteMonitoringList,
    PERMISSION.exportMonitoringList,
    PERMISSION.changeMonitoringList,
    PERMISSION.approveMonitoringList,
    PERMISSION.startRestartMonitoringTask,
    PERMISSION.viewMonitoringTasksList,
    PERMISSION.stopMonitoringTask,
    PERMISSION.sendNotificationContractors,
    PERMISSION.viewMonitoringResultsAllTypesPrice,
    PERMISSION.changeMonitoringResultsPrice,
    PERMISSION.manageMonitoringResultsYourComments,
    PERMISSION.exportMonitoringResults,
    PERMISSION.viewQuotas,
    PERMISSION.editQuotas,
    PERMISSION.configureMonitoringParameters,
    PERMISSION.markCorrectMonitoringResults
  ],

  [ROLE.contractor]: [PERMISSION.uploadMonitoringProductOrCompetitorLists, PERMISSION.downloadMonitoringResults],

  [ROLE.pricingSpecialist]: [
    PERMISSION.viewMonitoringListsRegistry,
    PERMISSION.exportMonitoringList,
    PERMISSION.changeMonitoringList,
    PERMISSION.recommendationToChangeMonitoringList,
    PERMISSION.viewMonitoringTasksList,
    PERMISSION.viewMonitoringResultsAllTypesPrice,
    PERMISSION.changeMonitoringResultsPrice,
    PERMISSION.manageMonitoringResultsYourComments,
    PERMISSION.exportMonitoringResults,
    PERMISSION.viewQuotas,
    PERMISSION.markCorrectMonitoringResults
  ],

  [ROLE.commercialServiceSpecialist]: [
    PERMISSION.viewMonitoringListsRegistry,
    PERMISSION.exportMonitoringList,
    PERMISSION.changeMonitoringList,
    PERMISSION.recommendationToChangeMonitoringListWithUserChoice,
    PERMISSION.viewMonitoringResultsAllTypesPrice,
    PERMISSION.manageMonitoringResultsYourComments,
    PERMISSION.exportMonitoringResults,
    PERMISSION.manageMonitoringResultsYourComments,
    PERMISSION.viewQuotas
  ],

  [ROLE.promoPlanningSpecialist]: [
    PERMISSION.viewMonitoringListsRegistry,
    PERMISSION.exportMonitoringList,
    PERMISSION.changeMonitoringList,
    PERMISSION.recommendationToChangeMonitoringList,
    PERMISSION.viewMonitoringResultsAllTypesPrice,
    PERMISSION.manageMonitoringResultsYourComments,
    PERMISSION.exportMonitoringResults,
    PERMISSION.viewQuotas
  ],

  [ROLE.commercialServiceDirector]: [
    PERMISSION.viewMonitoringListsRegistry,
    PERMISSION.exportMonitoringList,
    PERMISSION.changeMonitoringList,
    PERMISSION.recommendationToChangeMonitoringList,
    PERMISSION.viewMonitoringResultsAllTypesPrice,
    PERMISSION.manageMonitoringResultsYourComments,
    PERMISSION.exportMonitoringResults,
    PERMISSION.viewQuotas
  ],

  [ROLE.categoryManager]: [
    PERMISSION.viewMonitoringResultsAllTypesPrice,
    PERMISSION.manageMonitoringResultsYourComments,
    PERMISSION.exportMonitoringResults
  ]
}

const defaultAccessRoutes: Record<Role, PermittedRoute> = {
  [ROLE.serviceAccount]: REGISTRY_PAGE.registry,
  [ROLE.administrator]: REGISTRY_PAGE.registry,
  [ROLE.priceAnalyticsSpecialist]: REGISTRY_PAGE.registry,
  [ROLE.contractor]: CONTRACTOR_SECTION_PAGE.contractorSection,
  [ROLE.pricingSpecialist]: REGISTRY_PAGE.registry,
  [ROLE.commercialServiceSpecialist]: REGISTRY_PAGE.registry,
  [ROLE.promoPlanningSpecialist]: REGISTRY_PAGE.registry,
  [ROLE.commercialServiceDirector]: REGISTRY_PAGE.registry,
  [ROLE.categoryManager]: PROCESSING_RESULTS_PAGE.processingResults
}

export const getPermissionsByRole = (role: Role): Permission[] | undefined => rolesPermissions[role]

export const getDefaultPermittedRouteByRole = (role: Role): PermittedRoute | undefined => defaultAccessRoutes[role]

export const hasHelper = ({ superset, subset, checkFn }: HasHelperOptions): boolean => {
  if (isNull(superset)) return false

  const uniqueSubset = arrayUniqueValues(subset)

  return checkFn(superset, uniqueSubset)
}
